import { useState, FormEvent } from 'react';

export type FormValues = Record<string, string>;

export type FormErrors = Record<string, { message?: string }>;

type ValidationRule = {
  required?: {
    value: boolean;
    message: string;
  };
};

type Validations = Record<string, ValidationRule>;

interface UseFormOptions<T = FormValues> {
  onSubmit: (values: T) => void;
  validations?: Validations;
}

const getFormValues = (form: HTMLFormElement): FormValues => {
  const values: FormValues = {};
  const inputs = form.querySelectorAll('input, select, textarea');
  inputs.forEach(input => {
    const element = input as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    if (element.name) {
      values[element.name] = element.value;
    }
  });
  return values;
};

export const useForm = <T = FormValues>({ onSubmit, validations = {} }: UseFormOptions<T>) => {
  const [errors, setErrors] = useState<FormErrors>({});

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = getFormValues(event.currentTarget);

    const newErrors: FormErrors = Object.entries(validations).reduce((acc, [field, rules]) => {
      const value = values[field];
      if (rules.required?.value && (!value || String(value).trim() === '')) {
        return { ...acc, [field]: { message: rules.required.message } };
      }
      return acc;
    }, {} as FormErrors);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onSubmit(values as T);
  };

  return { handleSubmit, errors };
};
